import {
  FunctionNode,
  isBigNumber,
  isConstantNode,
  isFunctionNode,
  isOperatorNode,
  isSymbolNode,
  MathNode,
  SymbolNode
} from "mathjs";

import { SUPPORTED_FUNCTIONS } from "../consts";
import {
  add,
  constant,
  divide,
  getNodeChildren,
  isCommutative,
  multiply,
  unaryMinus
} from "../operations";
import { allImplicit, applyMinus, removeParentheses } from "../simplifications";

export function structEqual(a: MathNode, b: MathNode) {
  return structEqualHelper(prepareNode(a), prepareNode(b));
}

function structEqualHelper(a: MathNode, b: MathNode): boolean {
  if (a?.type !== b?.type) {
    return false;
  }

  if (isConstantNode(a) && isConstantNode(b)) {
    return a.value === b.value;
  }

  if (isSymbolNode(a) && isSymbolNode(b)) {
    return a.name === b.name;
  }

  if (isFunctionNode(a) && isFunctionNode(b)) {
    const aChildren = getNodeChildren(a);
    const bChildren = getNodeChildren(b);
    return aChildren.every((aChild, ind) =>
      structEqualHelper(aChild, bChildren[ind])
    );
  }

  if (isOperatorNode(a) && isOperatorNode(b)) {
    if (a.fn !== b.fn || a.args.length !== b.args.length) {
      return false;
    }

    const aChildren = getNodeChildren(a);
    const bChildren = getNodeChildren(b);

    if (isCommutative(a)) {
      const paired = Array.from(Array(aChildren.length)).map(_ => false);
      return aChildren.every(aChild =>
        bChildren.some((bChild, bInd) => {
          if (!paired[bInd] && structEqualHelper(aChild, bChild)) {
            paired[bInd] = true;
            return true;
          }
          return false;
        })
      );
    } else {
      return aChildren.every((aChild, ind) =>
        structEqualHelper(aChild, bChildren[ind])
      );
    }
  }

  throw new Error("unknown node type");
}

function prepareNode(node: MathNode) {
  const operations = [
    removeParentheses,
    allImplicit,
    applyImplicitMultiplications,
    replaceWithCommutative,
    applyMinus,
    flattenCommutative
  ];
  return operations.reduce((node, operation) => operation(node), node);
}

export function applyImplicitMultiplications(node: MathNode): MathNode {
  if (isSymbolNode(node)) {
    if (node.name.length === 1) {
      return node;
    }

    return multiply(
      new SymbolNode(node.name[0]),
      applyImplicitMultiplications(new SymbolNode(node.name.slice(1)))
    );
  }

  if (isFunctionNode(node)) {
    const { name } = node.fn;
    if (SUPPORTED_FUNCTIONS.find(fn => fn === name)) {
      return new FunctionNode(
        node.fn,
        node.args.map(applyImplicitMultiplications)
      );
    }
    if (!node.args[0]) return node;
    return multiply(
      applyImplicitMultiplications(new SymbolNode(name)),
      applyImplicitMultiplications(node.args[0])
    );
  }
  return node.map(applyImplicitMultiplications);
}

function replaceWithCommutative(node: MathNode): MathNode {
  const replacedNode = node.map(replaceWithCommutative);

  if (!isOperatorNode(replacedNode)) {
    return replacedNode;
  }

  // from a - b to a + (-b)
  if (replacedNode.fn === "subtract") {
    return add(replacedNode.args[0], unaryMinus(replacedNode.args[1]));
  }

  const isConstantWithValueOfOne =
    isConstantNode(replacedNode.args[0]) && replacedNode.args[0].value === 1;
  const isBigNumberWithValueOfOne =
    // @ts-expect-error typescript-5-upgrade
    isBigNumber(replacedNode.args[0]) && replacedNode.args[0].valueOf() === 1;
  // from a/b to a * 1/b
  if (
    replacedNode.fn === "divide" &&
    (!isConstantWithValueOfOne || !isBigNumberWithValueOfOne)
  ) {
    return multiply(
      replacedNode.args[0],
      divide(constant(1), replacedNode.args[1])
    );
  }

  return replacedNode;
}

/**
 * makes the children of commutative operations to seat at one level
 * so it's easier to compare
 */
export function flattenCommutative(node: MathNode): MathNode {
  const nodeWithFlattenedChildren = node.map(flattenCommutative);

  if (isCommutative(nodeWithFlattenedChildren)) {
    nodeWithFlattenedChildren.args = nodeWithFlattenedChildren.args.reduce(
      (nodes, childNode) =>
        nodes.concat(
          isOperatorNode(childNode) &&
            childNode.fn === nodeWithFlattenedChildren.fn
            ? childNode.args
            : [childNode]
        ),
      [] as MathNode[]
    );
  }

  return nodeWithFlattenedChildren;
}
